/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { AxiosError } from 'axios';
import { Formik, FormikValues } from 'formik';

import colors from '../../../theme/colors';
import { spacings } from '../../../theme/variables';
import Button from '../../Button/Button';
import alert from '../Alert';
import ButtonWithoutBorder from '../buttons/ButtonWithoutBorder';
import FlexBox from '../FlexBox';
import HotKey from '../HotKey';
import { CloseIcon } from '../icons';
import { BaseFormProps } from './BaseForm';

export const fullScreenFormCss = css`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  z-index: 1031;
  background: ${colors.white};
`;

const FullScreenForm = <T extends FormikValues>({
  renderForm,
  cancelHandler,
  closeHandler,
  header,
  footer,
  submitText,
  cancelText,
  cancelWithEscape,
  disabled,
  loading,
  pt,
  pr,
  pb,
  pl,
  controlsAlignment,
  controlsWidth,
  submitVariant,
  cancelVariant,
  customControls,
  buttonsSize,
  cancelTestId,
  submitTestId,
  formTestId,
  revertButtonsPosition,
  ...rest
}: BaseFormProps<T>) => {
  return (
    <Formik {...rest}>
      {formikProps => {
        return (
          <form
            data-testid={formTestId}
            onSubmit={e => {
              e.preventDefault();
              e.stopPropagation();
              formikProps.submitForm().catch((e: Error) => {
                if (e instanceof AxiosError) {
                  // Do nothing, handled in interceptor
                } else {
                  alert({ title: 'An unexpected error occurred', message: e.message }).error();
                  // For debugging purposes
                  // eslint-disable-next-line no-console
                  console.log(e);
                }
              });
            }}
            css={fullScreenFormCss}
          >
            {header && (
              <FlexBox
                p={24}
                justifySpaceBetween
                alignItemsCenter
                customCss={css`
                  border-bottom: 1px solid ${colors.border};
                `}
              >
                {typeof header === 'function' ? header(formikProps) : header}
                {(closeHandler || cancelHandler) && (
                  <FlexBox alignItemsCenter>
                    {cancelWithEscape && (
                      <HotKey
                        message="Press ESC"
                        customCss={css`
                          margin-right: ${spacings.px8}px;
                        `}
                      />
                    )}
                    <ButtonWithoutBorder
                      onClick={closeHandler || cancelHandler}
                      disabled={formikProps.isSubmitting}
                      aria-label="Close"
                    >
                      <CloseIcon color={colors.black} width={40} height={40} />
                    </ButtonWithoutBorder>
                  </FlexBox>
                )}
              </FlexBox>
            )}
            <FlexBox
              fitParentHeight
              columnDirection
              customCss={css`
                overflow-x: scroll;
                padding: ${spacings.px24}px;
              `}
            >
              {renderForm(formikProps)}
            </FlexBox>
            {!customControls && (
              <FlexBox
                customCss={css`
                  justify-content: ${controlsAlignment === 'right' ? 'end' : 'start'};
                  gap: ${spacings.px24}px;
                  border-top: 1px solid ${colors.border};
                  padding: ${spacings.px24}px;
                `}
              >
                {footer && (
                  <FlexBox
                    alignItemsCenter
                    css={css`
                      width: 100%;
                    `}
                  >
                    {typeof footer === 'function' ? footer(formikProps) : footer}
                  </FlexBox>
                )}

                <FlexBox
                  gap={spacings.px8}
                  css={css`
                    flex-direction: ${revertButtonsPosition ? 'row-reverse' : 'row'};
                  `}
                >
                  {cancelHandler && (
                    <Button
                      data-testid={cancelTestId}
                      variant={cancelVariant}
                      size={buttonsSize}
                      customCss={css`
                        color: ${cancelVariant ? colors.blue400 : colors.neutral600};
                        min-width: ${controlsWidth}px;
                      `}
                      type="button"
                      disabled={formikProps.isSubmitting}
                      onClick={cancelHandler}
                    >
                      {cancelText || 'Cancel'}
                    </Button>
                  )}

                  <Button
                    data-testid={submitTestId}
                    disabled={
                      formikProps.isSubmitting ||
                      (typeof disabled === 'function' ? disabled(formikProps) : disabled) ||
                      loading
                    }
                    loading={formikProps.isSubmitting || formikProps.isValidating || loading}
                    type="submit"
                    variant={submitVariant}
                    size={buttonsSize}
                    customCss={css`
                      min-width: ${controlsWidth}px;
                    `}
                  >
                    {typeof submitText === 'function' ? submitText(formikProps) : submitText || 'Create'}
                  </Button>
                </FlexBox>
              </FlexBox>
            )}
          </form>
        );
      }}
    </Formik>
  );
};

export default FullScreenForm;
